"use client";

import { Fragment } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { useNow } from "@/features/contests/lib/use-now";
import { formatRemaining, remainingUntil } from "@/features/contests/lib/format";
import styles from "./arena.module.css";

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * The round card's flip clock (`EX-PUB19`): days, hours, minutes and seconds
 * to the start, or hours, minutes and seconds to the end of a live round.
 *
 * Each digit is keyed by its position and its value, so a digit that changes
 * mounts afresh and plays its 220 ms roll, and a digit that did not change
 * keeps its node and stays still. The clock reads nothing on the server (a
 * server-rendered "starts in" would be wrong for everyone who arrives a minute
 * later), so the digits read "--" until the first tick.
 */
export function RoundCountdown({ target, mode }: { target: string | null; mode: "start" | "end" }) {
  const t = useTranslations("judge.home");
  const tDirectory = useTranslations("contest.directory");
  const now = useNow();
  const remaining = now === null ? null : remainingUntil(target, now);

  const groups: Array<{ value: string; label: string }> =
    mode === "start"
      ? [
          { value: remaining ? pad(remaining.days) : "--", label: t("countdownDays") },
          { value: remaining ? pad(remaining.hours) : "--", label: t("countdownHours") },
          { value: remaining ? pad(remaining.minutes) : "--", label: t("countdownMinutes") },
          { value: remaining ? pad(remaining.seconds) : "--", label: t("countdownSeconds") },
        ]
      : [
          { value: remaining ? pad(remaining.days * 24 + remaining.hours) : "--", label: t("countdownHours") },
          { value: remaining ? pad(remaining.minutes) : "--", label: t("countdownMinutes") },
          { value: remaining ? pad(remaining.seconds) : "--", label: t("countdownSeconds") },
        ];

  const spoken = remaining
    ? `${mode === "start" ? tDirectory("startsIn") : tDirectory("endsIn")} ${formatRemaining(remaining)}`
    : undefined;

  return (
    <div role="timer" aria-label={spoken} className="flex items-start gap-1.5 sm:gap-2.5">
      {groups.map((group, groupIndex) => (
        <Fragment key={group.label}>
          {groupIndex > 0 ? (
            <span
              className="flex h-[50px] items-center text-2xl font-bold text-neutral-500 sm:h-[72px] sm:text-4xl"
              aria-hidden
            >
              :
            </span>
          ) : null}
          <div className="flex flex-col items-center gap-2.5" aria-hidden>
            <div className="flex gap-1 sm:gap-1.5">
              {group.value.split("").map((char, index) => (
                <span
                  key={`${index}-${char}`}
                  className={cn(
                    styles.digit,
                    styles.mono,
                    "flex h-[50px] w-[34px] items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.06] text-[28px] font-bold text-white sm:h-[72px] sm:w-[52px] sm:rounded-[10px] sm:text-[42px]",
                  )}
                >
                  <span className={styles.rollA}>{char}</span>
                </span>
              ))}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-400 sm:text-[11px] sm:tracking-[0.14em]">
              {group.label}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
